import { useState } from "react";
import { api } from "../../services/api/endpoints";
import type { ModelId, TierId } from "../../services/api/schemas";

/**
 * Starts a checkout.
 *
 * Two things here are deliberate and easy to get wrong:
 *
 * 1. The session is created SERVER-SIDE and we only receive a URL. No Stripe key
 *    ever reaches the client — not even the publishable one.
 *
 * 2. We `window.location.assign()` the URL and treat it as OPAQUE. A real Stripe
 *    URL is cross-origin, so routing to it is impossible; and the full page leave
 *    is not an accident, it's the thing that proves session restore works. If you
 *    "optimise" this into a client-side navigation, the mock will pass and
 *    production will log the user out immediately after they pay.
 */
export function useCheckout() {
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start(
    grants: 
      | { kind: "model"; modelId: ModelId }
      | { kind: "tier"; tier: Exclude<TierId, "free"> }
      /* The pack takes no parameter: quantity and price are server
         constants, so a request cannot ask for a bigger one. */
      | { kind: "topup" },
    priceId: string,
    next: string,
  ) {
    setStarting(true);
    setError(null);
    try {
      const { url } = await api.checkoutSession({ priceId, grants, next });
      window.location.assign(url);
    } catch {
      setError("Couldn't start checkout. Try again in a moment.");
      setStarting(false);
    }
  }

  return { start, starting, error };
}
